import { useEffect, useMemo, useState } from 'react';
import { movieCatalog, genres, kindTabs } from '@/mocks/movies';
import type { Movie } from '@/pages/home/types';
import MovieCard from '@/pages/home/components/MovieCard';
import SectionHeading from '@/pages/home/components/SectionHeading';

interface LibrarySectionProps {
  query: string;
  setQuery: (value: string) => void;
  genre: string;
  setGenre: (value: string) => void;
  kind: string;
  setKind: (value: string) => void;
}

const PAGE_SIZE = 8;

export default function LibrarySection({
  query,
  setQuery,
  genre,
  setGenre,
  kind,
  setKind,
}: LibrarySectionProps) {
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const filtered = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    return (movieCatalog as Movie[]).filter((m) => {
      if (genre !== '全部' && m.genre !== genre) return false;
      if (kind === '电影' && m.kind !== 'movie') return false;
      if (kind === '剧集' && m.kind !== 'tv') return false;
      if (keyword) {
        const haystack = `${m.title} ${m.genre} ${m.region} ${m.tags.join(' ')}`.toLowerCase();
        if (!haystack.includes(keyword)) return false;
      }
      return true;
    });
  }, [query, genre, kind]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [query, genre, kind]);

  const visible = filtered.slice(0, visibleCount);
  const hasFilter = genre !== '全部' || kind !== '全部' || query.trim() !== '';

  const handleReset = () => {
    setGenre('全部');
    setKind('全部');
    setQuery('');
  };

  return (
    <section id="library" className="bg-background-100">
      <div className="mx-auto max-w-[1280px] px-4 py-14 md:px-6 md:py-20">
        <SectionHeading
          eyebrow="片库检索"
          title="电影大全与电视剧，按类型与关键词一站筛选"
          desc="片库同时收录电影与剧集，你可以先点类型标签缩小范围，再切换到电影或剧集，也可以直接输入片名关键词定位目标内容。"
        />

        <div className="mt-8 flex flex-col gap-5 rounded-lg border border-background-200 bg-background-50 p-4 md:p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
              {kindTabs.map((tab) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setKind(tab)}
                  className={`cursor-pointer rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                    kind === tab
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-700 hover:text-primary-700'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>

            <div className="relative w-full lg:w-80">
              <span className="pointer-events-none absolute left-3 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-foreground-400">
                <i className="ri-search-line text-lg"></i>
              </span>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="搜索片名或关键词"
                aria-label="在片库中搜索"
                className="h-11 w-full rounded-md border border-background-300 bg-background-50 pl-10 pr-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {genres.map((g) => (
              <button
                key={g}
                type="button"
                onClick={() => setGenre(g)}
                className={`cursor-pointer rounded-full px-3.5 py-1.5 text-sm transition-colors ${
                  genre === g
                    ? 'bg-accent-500 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-secondary-100 hover:text-secondary-900'
                }`}
              >
                {g}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-background-200 pt-4">
            <span className="text-sm text-foreground-600">
              共找到 <strong className="text-accent-700">{filtered.length}</strong> 条内容
              {hasFilter ? '（已按条件筛选）' : ''}
            </span>
            {hasFilter && (
              <button
                type="button"
                onClick={handleReset}
                className="flex cursor-pointer items-center gap-1 rounded-md border border-background-300 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                <i className="ri-refresh-line"></i>
                清空筛选
              </button>
            )}
          </div>
        </div>

        {visible.length > 0 ? (
          <>
            <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {visible.map((m) => (
                <MovieCard key={m.id} movie={m} onOpenGenre={setGenre} />
              ))}
            </div>
            {visibleCount < filtered.length && (
              <div className="mt-10 flex justify-center">
                <button
                  type="button"
                  onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-primary-300 bg-primary-50 px-6 py-3 text-sm font-medium text-primary-700 transition-colors hover:bg-primary-100"
                >
                  <i className="ri-add-line text-lg"></i>
                  查看更多片库内容（剩余 {filtered.length - visibleCount} 条）
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="mt-10 flex flex-col items-center gap-3 rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-emotion-sad-line text-2xl"></i>
            </span>
            <p className="font-heading text-lg font-bold text-foreground-900">没有找到匹配的片库内容</p>
            <p className="text-sm text-foreground-600">试着换个关键词，或者清空筛选条件看看全部内容。</p>
            <button
              type="button"
              onClick={handleReset}
              className="mt-2 cursor-pointer rounded-md bg-primary-500 px-5 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              清空筛选
            </button>
          </div>
        )}
      </div>
    </section>
  );
}